import { User } from "@prisma/client";
import Stepper from "immigram-ui-cmon/src/components/Stepper/Stepper";
import findIndex from "lodash/findIndex";
import { useTranslation } from "next-i18next";
import { useMemo } from "react";

import { getApplicationSection } from "@/lib/application";
import { ApplicationSectionStatusType } from "@/lib/applicationSectionStatus";
import { isAdmin } from "@/lib/auth/rbac";
import { AppPages, getApplicationRelLink } from "@/lib/pages";
import { ApplicationFull } from "@/lib/prisma";

export enum ProfJourneyStepperSlug {
  Potential = "potential",
  Details = "details",
}

interface ProfJourneyStepperProps {
  application: ApplicationFull;
  current: ProfJourneyStepperSlug;
  ownUser: User;
}

export function ProfJourneyStepper({
  ownUser,
  application,
  current,
}: ProfJourneyStepperProps) {
  const { t } = useTranslation();

  const applicationSection = getApplicationSection(application);
  const isOutlineCompleted =
    applicationSection?.profJourneyOutlineStatusId ===
    ApplicationSectionStatusType.ReviewCompleted;
  const isDetailsCompleted =
    applicationSection?.profJourneyStatusId ===
    ApplicationSectionStatusType.ReviewCompleted;

  const steps = useMemo(
    () => [
      {
        title: t("application:profJourney.stepper.potential.title"),
        slug: ProfJourneyStepperSlug.Potential,
        isSucceed: isOutlineCompleted,
        url: getApplicationRelLink({
          applicationId: application.id,
          link: AppPages.ApplicationProfileProfJourneyPotential,
        }),
      },
      {
        title: t("application:profJourney.stepper.details.title"),
        slug: ProfJourneyStepperSlug.Details,
        isSucceed: isOutlineCompleted && isDetailsCompleted,
        disabled: !isAdmin(ownUser) && !isOutlineCompleted,
        url: getApplicationRelLink({
          applicationId: application.id,
          link: AppPages.ApplicationProfileProfJourneyDetails,
        }),
      },
    ],
    [application.id, ownUser, isOutlineCompleted, isDetailsCompleted, t]
  );

  return (
    <Stepper
      className="mt-3"
      currentIndex={findIndex(steps, (step) => step.slug === current)}
      steps={steps}
    />
  );
}
